/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import styled from '@emotion/styled';
import React, { FormEvent } from 'react';

import { Link as ReactRouterDOMLink } from 'react-router-dom';
import Field from 'redux-form/lib/Field';
import reduxForm from 'redux-form/lib/reduxForm';

import ButtonWithHoverAndPress from '../../shared/Button/ButtonWithHoverAndPress';
import { LinkText } from '../../shared/Link';
import { Text } from '../../shared/Text';
import Captcha from '../Captcha';
import { FieldInputWithRedux } from '../FieldInput';
import events from '../SignupPageContainer/events';
import ErrorAlert from './components/ErrorAlert';

import Box from '../../shared/Box';
import {
  EXISTING_ACCOUNT_ERROR_MESSAGE,
  EXISTING_EMAIL_ERROR_MESSAGE,
  PASSWORD_ERROR_MESSAGE,
  validate,
} from '../../user';
// @ts-ignore
import styles from '../OnboardingRouter/styles.sass';

const INPUT_FIELDS = [
  {
    name: 'name',
    placeholder: 'Full Name',
    type: 'text',
  },
  {
    name: 'email',
    placeholder: 'Email',
    type: 'email',
  },
  {
    name: 'password',
    placeholder: 'Password',
    type: 'password',
  },
];

type Props = {
  error?: string;
  feedUrl?: string;
  handleSubmit: (data: FormEvent<HTMLFormElement>) => void;
  invalid: boolean;
  onClickRequestResetPassword: () => void;
  isLoading?: boolean;
  submitting: boolean;
  isV3Captcha?: boolean;
  loginUrl?: string;
  isImporting: boolean;
};

// This is currently built only for onboarding
const SignupForm = ({
  error,
  feedUrl = '',
  handleSubmit,
  submitting,
  invalid,
  onClickRequestResetPassword,
  isLoading = false,
  isV3Captcha = false,
  loginUrl,
  isImporting,
}: Props) => {
  const isSubmitDisabled = isLoading || submitting || invalid;

  const handleClickRequestResetPassword = () => {
    onClickRequestResetPassword();
  };

  const getLoginUrl = () => {
    return loginUrl
      ? loginUrl
      : `/login${feedUrl ? `?feedUrl=${encodeURIComponent(feedUrl)}` : ''}`;
  };

  return (
    <form
      onSubmit={data => {
        if (!isSubmitDisabled) handleSubmit(data);
      }}
      id="SignupForm"
      method="POST"
    >
      <FieldsContainer>
        {INPUT_FIELDS.map(field => (
          <FieldWrapper>
            <Field
              name={field.name}
              placeholder={field.placeholder}
              component={FieldInputWithRedux}
              type={field.type}
              key={field.name}
              autofocus={field.name === 'name'}
            />
          </FieldWrapper>
        ))}
        <CatpchaWrapper>
          <Field
            name="captcharesponse"
            isV3={isV3Captcha}
            component={Captcha}
          />
        </CatpchaWrapper>
      </FieldsContainer>
      {error && (
        <Box display="flex" justifyContent="center">
          <Box maxWidth={404} width="100%">
            {(() => {
              switch (error) {
                case EXISTING_ACCOUNT_ERROR_MESSAGE:
                  return (
                    <ErrorAlert
                      title="Existing account found"
                      renderMessage={
                        <React.Fragment>
                          We found an existing account that uses these
                          credentials. You can{' '}
                          <ReactRouterDOMLink
                            to={getLoginUrl()}
                            onClick={() => events.signUpSignInButtonClicked()}
                          >
                            log in
                          </ReactRouterDOMLink>{' '}
                          ‌or try again with a different email address.
                        </React.Fragment>
                      }
                    />
                  );
                case EXISTING_EMAIL_ERROR_MESSAGE:
                  return (
                    <ErrorAlert
                      title="This email address is already being used"
                      renderMessage={
                        <React.Fragment>
                          We found an existing account with this email address,
                          but this password doesn’t match. You can try again or{' '}
                          <ReactRouterDOMLink
                            to={getLoginUrl()}
                            onClick={handleClickRequestResetPassword}
                          >
                            reset your password
                          </ReactRouterDOMLink>
                          .
                        </React.Fragment>
                      }
                    />
                  );
                case PASSWORD_ERROR_MESSAGE:
                  return <ErrorAlert title={error} />;
                default:
                  return (
                    <ErrorAlert
                      title="Unable to create account"
                      renderMessage={
                        <React.Fragment>
                          Sorry, but we were unable to create your account.
                          Please refresh the page and try again, or visit{' '}
                          <LinkText
                            to="https://help.anchor.fm/hc/en-us"
                            isInline={true}
                          >
                            help.anchor.fm
                          </LinkText>{' '}
                          so we can help you troubleshoot.
                        </React.Fragment>
                      }
                    />
                  );
              }
            })()}
          </Box>
        </Box>
      )}
      <ButtonWrapper>
        <ButtonWithHoverAndPress
          colorTheme="primary"
          isDisabled={isSubmitDisabled}
          isProcessing={submitting || isLoading}
          isFullWidth={true}
          size="sm"
          shape="pill"
          text="Sign up"
          type="unelevated"
          typeAttribute="submit"
          isEventBubblingAllowed={!isSubmitDisabled}
          onPress={events.signUpRegisterButtonClicked}
        />
      </ButtonWrapper>
      <div className={styles.terms}>
        <React.Fragment>
          {isImporting && (
            <div
              css={css`
                margin-bottom: 32px;
              `}
            >
              <Text size="md" align="center">
                Already have an account?{' '}
                <Text size="md" isBold={true} isInline={true}>
                  <ReactRouterDOMLink to={getLoginUrl()}>
                    Log in
                  </ReactRouterDOMLink>{' '}
                </Text>
                instead.
              </Text>
            </div>
          )}
          By continuing, you agree to our{' '}
          <LinkText
            target="_blank"
            onClick={() => events.signUpLegalLinkClicked({ link: 'tos' })}
            isInline={true}
            to="/tos"
            color="gray"
          >
            Terms of Service
          </LinkText>{' '}
          and{' '}
          <LinkText
            target="_blank"
            onClick={() => events.signUpLegalLinkClicked({ link: 'privacy' })}
            isInline={true}
            color="gray"
            to="/privacy"
          >
            Privacy Policy
          </LinkText>
          .
        </React.Fragment>
      </div>
    </form>
  );
};

const FieldsContainer = styled.div`
  max-width: 410px;
  width: 100%;
  margin: auto;
`;

const FieldWrapper = styled.div`
  margin-bottom: 10px;
`;
const CatpchaWrapper = styled.div`
  margin-top: 24px;
`;

const ButtonWrapper = styled.div`
  margin: auto;
  margin-bottom: 14px;
  margin-top: 32px;
  max-width: 300px;
  width: 100%;
`;

export default reduxForm<
  { name: string; email: string; password: string; captcharesponse: string },
  Props,
  boolean
>({
  form: 'createAccount',
  validate,
})(SignupForm);
